import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  PrimaryButton,
  DefaultButton,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
  Dropdown,
  IDropdownOption,
  Label,
  Separator,
  TextField,
} from '@fluentui/react';
import { extractEmailData, downloadAttachments, showNotification } from '../../services/officeService';
import { apiClient } from '../../services/apiClient';
import './App.css';

interface EmailInfo {
  subject: string;
  senderEmail: string;
  senderName: string;
  receivedDate: Date;
  attachmentCount: number;
}

interface Client {
  id: number;
  klantnaam: string;
  klantnummer: string;
  email_algemeen?: string;
  telefoon_algemeen?: string;
  status?: string;
  isactive?: number;
  created_at?: string;
}

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emailInfo, setEmailInfo] = useState<EmailInfo | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [sharepointFolderPath, setSharepointFolderPath] = useState<string>('');
  const [isLoadingSharePoint, setIsLoadingSharePoint] = useState<boolean>(false);
  const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);

  /**
   * Initialize authentication and Graph client on mount
   */
  useEffect(() => {
    initializeAuth();
  }, []);

  /**
   * Initialize - load email data when component mounts
   */
  useEffect(() => {
    if (isAuthInitialized) {
      loadEmailData();
    }
  }, [isAuthInitialized]);

  /**
   * Fetch SharePoint folder path when selected client changes
   */
  useEffect(() => {
    const fetchSharePointPath = async () => {
      if (selectedClientId) {
        setIsLoadingSharePoint(true);
        try {
          const result = await apiClient.getClientSharePointPath(selectedClientId);
          if (result.success) {
            setSharepointFolderPath(result.webUrl || result.folderPath);
            
            // Show info message if folder was created
            if (result.created) {
              setMessage({
                text: `SharePoint map aangemaakt: ${result.folderPath}`,
                type: MessageBarType.info,
              });
            }
          }
        } catch (error: any) {
          console.error('Error fetching SharePoint path:', error);
          setSharepointFolderPath('');
          setMessage({
            text: 'Waarschuwing: SharePoint map kon niet worden opgehaald',
            type: MessageBarType.warning,
          });
        } finally {
          setIsLoadingSharePoint(false);
        }
      } else {
        setSharepointFolderPath('');
        setIsLoadingSharePoint(false);
      }
    };

    fetchSharePointPath();
  }, [selectedClientId]);

  /**
   * Initialize authentication - load token from storage and fetch Graph token
   */
  const initializeAuth = async () => {
    try {
      // Load existing auth token from storage
      const authToken = apiClient.getToken();
      
      if (authToken) {
        console.log('Auth token loaded from storage');
      }

      // Initialize Microsoft Graph client and fetch token
      const graphResult = await apiClient.initializeGraphClient();
      
      if (graphResult.success) {
        console.log('Microsoft Graph client initialized successfully');
        setIsAuthInitialized(true);
      } else {
        console.warn('Failed to initialize Graph client:', graphResult.message);
        // Set initialized anyway to allow the app to continue
        setIsAuthInitialized(true);
        setMessage({
          text: 'Waarschuwing: Graph authenticatie niet geïnitialiseerd',
          type: MessageBarType.warning,
        });
      }
    } catch (error: any) {
      console.error('Error initializing auth:', error);
      // Allow app to continue even if Graph initialization fails
      setIsAuthInitialized(true);
      setMessage({
        text: 'Waarschuwing: Authenticatie kon niet volledig worden geïnitialiseerd',
        type: MessageBarType.warning,
      });
    }
  };

  /**
   * Load email data from currently selected email
   */
  const loadEmailData = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      // Extract email metadata
      const emailData = await extractEmailData();
      
      setEmailInfo({
        subject: emailData.subject,
        senderEmail: emailData.senderEmail,
        senderName: emailData.senderName,
        receivedDate: emailData.receivedDate,
        attachmentCount: emailData.attachmentCount,
      });

      // Match sender email with clients
      await matchClients(emailData.senderEmail);
    } catch (error: any) {
      console.error('Error loading email data:', error);
      setMessage({
        text: `Fout bij laden e-mail: ${error.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Match sender email with clients in the system
   */
  const matchClients = async (senderEmail: string) => {
    try {
      const result = await apiClient.getKlantenByEmail(senderEmail);
        console.log('Match clients result:', result);
      if (result.success && result.klanten && result.klanten.length > 0) {
        setClients(result.klanten);

        // Auto-select if only one match
        if (result.klanten.length === 1) {
          setSelectedClientId(result.klanten[0].id);
          setMessage({
            text: `Klant gevonden: ${result.klanten[0].klantnaam} (${result.count} resultaat)`,
            type: MessageBarType.success,
          });
        } else {
          setMessage({
            text: `${result.count} klanten gevonden voor ${result.companyName}. Selecteer de juiste klant.`,
            type: MessageBarType.info,
          });
        }
      } else {
        // No matches found - load all clients for manual selection
        setMessage({
          text: 'Geen klant gevonden op basis van het e-mailadres van de contactpersoon.',
          type: MessageBarType.warning,
        });
        // await loadAllClients();
      }
    } catch (error: any) {
      console.error('Error matching clients:', error);
      setMessage({
        text: 'Fout bij zoeken naar klant. Selecteer handmatig.',
        type: MessageBarType.warning,
      });
      // await loadAllClients();
    }
  };

  /**
   * Load all clients for manual selection
   */
  const loadAllClients = async () => {
    try {
      const result = await apiClient.getClients();
      if (result.success) {
        setClients(result.clients);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  /**
   * Handle saving the email
   */
  const handleSaveEmail = async () => {
    // Validation: No client selected
    if (!selectedClientId) {
      setMessage({
        text: 'Selecteer eerst een klant om verder te gaan.',
        type: MessageBarType.error,
      });
      return;
    }

    // Validation: Check if SharePoint folder is available
    if (!sharepointFolderPath) {
      setMessage({
        text: 'De SharePoint-map voor deze klant is niet beschikbaar.',
        type: MessageBarType.error,
      });
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    try {
      // Extract full email data
      const emailData = await extractEmailData();
      console.log('Email data to save:', emailData);
      
      // Download attachments
      const attachments = await downloadAttachments();
      console.log('Attachments downloaded:', attachments);
      
      const model = {
        clientId: selectedClientId,
        email: emailData,
        attachment: attachments,
      };
      console.log('Model to save:', model);

      // Save to backend
      const result = await apiClient.saveEmailAttachments(model);
      console.log('Save email result:', result);
      if (result.success) {
        const attachmentCount = result.attachmentCount || 0;
        const sharepointUploaded = result.sharepointUploaded || 0;
        
        // Determine appropriate success message
        let messageText: string;
        let messageType: MessageBarType;
        let notificationMessage: string;
        
        if (attachmentCount === 0) {
          // Success – Email Only (No Attachment)
          messageText = 'E-mail is succesvol opgeslagen. Geen bijlagen gevonden.';
          messageType = MessageBarType.success;
          notificationMessage = 'E-mail succesvol opgeslagen';
        } else if (sharepointUploaded === attachmentCount) {
          // Success – Email & Attachment (all uploaded)
          messageText = 'E-mail en bijlagen zijn succesvol opgeslagen bij de geselecteerde klant.';
          messageType = MessageBarType.success;
          notificationMessage = 'E-mail en bijlagen succesvol opgeslagen';
        } else if (sharepointUploaded > 0 && sharepointUploaded < attachmentCount) {
          // Success – Partial (Some Attachments failed)
          messageText = 'E-mail is opgeslagen. Sommige bijlagen konden niet worden opgeslagen in SharePoint.';
          messageType = MessageBarType.warning;
          notificationMessage = 'E-mail opgeslagen met waarschuwing';
        } else {
          // All attachments failed to upload to SharePoint
          messageText = 'Bijlagen konden niet worden geüpload naar SharePoint.';
          messageType = MessageBarType.error;
          notificationMessage = 'Fout bij uploaden bijlagen';
        }

        setMessage({
          text: messageText,
          type: messageType,
        });

        // Show Outlook notification
        showNotification(notificationMessage, messageType === MessageBarType.error ? 'error' : 'info');

        // Reset form after 2 seconds if success or warning
        if (messageType !== MessageBarType.error) {
          setTimeout(() => {
            resetForm();
          }, 2000);
        }
      } else {
        throw new Error(result.message || 'Opslaan mislukt');
      }
      
    } catch (error: any) {
      console.error('Error saving email:', error);
      
      // Determine specific error message
      let errorMessage = 'Fout bij opslaan: ';
      
      if (error.response?.status === 404) {
        errorMessage = 'De SharePoint-map voor deze klant is niet beschikbaar.';
      } else if (error.message?.includes('SharePoint')) {
        errorMessage = 'Bijlagen konden niet worden geüpload naar SharePoint.';
      } else {
        errorMessage += error.message || 'Onbekende fout opgetreden';
      }
      
      setMessage({
        text: errorMessage,
        type: MessageBarType.error,
      });
      showNotification('Fout bij opslaan e-mail', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    resetForm();
  };

  /**
   * Reset form to initial state
   */
  const resetForm = () => {
    setEmailInfo(null);
    setClients([]);
    setSelectedClientId(null);
    setNotes('');
    setSharepointFolderPath('');
    setIsLoadingSharePoint(false);
    setMessage(null);
    loadEmailData();
  };

  /**
   * Get client dropdown options
   */
  const getClientOptions = (): IDropdownOption[] => {
    return clients.map((client: Client) => ({
      key: client.id,
      text: `${client.klantnaam} - ${client.klantnummer}`,
    }));
  };

  // Show loading spinner
  if (isLoading) {
    return (
      <div className="app-container">
        <Stack horizontalAlign="center" verticalAlign="center" style={{ height: '100%' }}>
          <Spinner size={SpinnerSize.large} label="E-mail laden..." />
        </Stack>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Stack tokens={{ childrenGap: 15 }}>
        {/* Header */}
        <Stack.Item>
          <Text variant="xLarge" block>
            E-mail aan klant koppelen
          </Text>
        </Stack.Item>

        <Separator />

        {/* Message bar */}
        {message && (
          <Stack.Item>
            <MessageBar
              messageBarType={message.type}
              isMultiline={false}
              onDismiss={() => setMessage(null)}
            >
              {message.text}
            </MessageBar>
          </Stack.Item>
        )}

        {/* Email info */}
        {/* {emailInfo && (
          <Stack.Item>
            <Stack tokens={{ childrenGap: 8 }}>
              <Label>E-mail informatie</Label>
              <Text>
                <strong>Onderwerp:</strong> {emailInfo.subject}
              </Text>
              <Text>
                <strong>Van:</strong> {emailInfo.senderName} ({emailInfo.senderEmail})
              </Text>
              <Text>
                <strong>Datum:</strong> {new Date(emailInfo.receivedDate).toLocaleString('nl-NL')}
              </Text>
              <Text>
                <strong>Bijlagen:</strong> {emailInfo.attachmentCount}
              </Text>
            </Stack>
          </Stack.Item>
        )} */}

        {/* <Separator /> */}

        {/* Client selection */}
        <Stack.Item>
          <Dropdown
            label="Selecteer klant"
            placeholder="Kies een klant"
            options={getClientOptions()}
            selectedKey={selectedClientId}
            onChange={(_, option) => setSelectedClientId(option?.key as number)}
            required
            disabled={isProcessing}
          />
        </Stack.Item>

        {/* Loading SharePoint path - only show if there are attachments */}
        {isLoadingSharePoint && emailInfo && emailInfo.attachmentCount > 0 && (
          <Stack.Item>
            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
              <Spinner size={SpinnerSize.small} />
              <Text>SharePoint map laden...</Text>
            </Stack>
          </Stack.Item>
        )}

        {/* SharePoint folder path - shown when client is selected and email has attachments */}
        {selectedClientId && sharepointFolderPath && !isLoadingSharePoint && emailInfo && emailInfo.attachmentCount > 0 && (
          <Stack.Item>
            <TextField
              label="SharePoint Map"
              value={sharepointFolderPath}
              readOnly
               multiline
              rows={3}
              disabled={isProcessing}
            />
          </Stack.Item>
        )}

        {/* No attachments message */}
        {emailInfo && emailInfo.attachmentCount === 0 && selectedClientId && (
          <Stack.Item>
            <MessageBar messageBarType={MessageBarType.info}>
              Deze e-mail heeft geen bijlagen om te uploaden.
            </MessageBar>
          </Stack.Item>
        )}
        {/* Processing indicator */}
        {isProcessing && (
          <Stack.Item>
            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
              <Spinner size={SpinnerSize.medium} />
              <Text>E-mail en bijlagen worden opgeslagen...</Text>
            </Stack>
          </Stack.Item>
        )}

        {/* Action buttons */}
        <Stack.Item>
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <PrimaryButton
              text={isProcessing ? 'Bezig...' : 'Opslaan'}
              onClick={handleSaveEmail}
              disabled={!selectedClientId || isProcessing}
              iconProps={{ iconName: 'Save' }}
            />
            <DefaultButton
              text="Annuleren"
              onClick={handleCancel}
              disabled={isProcessing}
              iconProps={{ iconName: 'Cancel' }}
            />
          </Stack>
        </Stack.Item>
      </Stack>
    </div>
  );
};

export default App;
